import { fetchAuthSession } from 'aws-amplify/auth';
import { EMISSIONS_API_URL } from "../../utils/apiConfig";
import { useEffect, useMemo, useRef, useState } from 'react';
import { buildCSV } from "../../utils/buildCSV";
import { cleanNumericInput, formatNumericInput } from "../../utils/numberInput";
import {
  showAppNotification,
  showProcessingErrorNotification,
  showProcessingNotification,
  showProcessingSuccessNotification,
} from "../../utils/notifications";

const BRAND = "#004A53";
const PRIMARY = "#005654";
const INTERACTION = "#026F6D";
const GREEN_SURFACE = "#f0f7f6";

const TEXT = "#0f172a";
const MUTED = "#64748b";
const SOFT_BORDER = "#e2e8f0";

function applyGreenOneSdxButtonStyles(root) {
  if (!root) return;

  function styleButton(button) {
    const shadowRoot = button.shadowRoot;

    if (!shadowRoot) return;

    if (
      shadowRoot.querySelector(
        "[data-greenone-button-styles]"
      )
    ) {
      return;
    }

    const style = document.createElement("style");

    style.setAttribute(
      "data-greenone-button-styles",
      "true"
    );

    style.textContent = `
      button.primary {
        background: ${PRIMARY} !important;
        border-color: ${PRIMARY} !important;
        color: #fff !important;
      }

      button.primary:hover {
        background: ${INTERACTION} !important;
        border-color: ${INTERACTION} !important;
        color: #fff !important;
      }

      button.secondary {
        background: #fff !important;
        border-color: ${INTERACTION} !important;
        color: ${INTERACTION} !important;
      }

      button.secondary:hover {
        background: ${INTERACTION} !important;
        border-color: ${INTERACTION} !important;
        color: #fff !important;
      }

      button.primary:focus-visible,
      button.secondary:focus-visible {
        outline: 3px solid ${PRIMARY} !important;
        outline-offset: 2px !important;
      }
    `;

    shadowRoot.appendChild(style);
  }

  function applyStyles() {
    root
      .querySelectorAll("sdx-button")
      .forEach(styleButton);
  }

  applyStyles();

  const observer = new MutationObserver(applyStyles);

  observer.observe(root, {
    childList: true,
    subtree: true
  });

  return () => observer.disconnect();
}

/* Helpers */
const _sdxRaw = (e) => e?.detail?.value ?? e?.target?.value ?? e;


// Einzelwert (String) – für normale Selects
export const readSdxValue = (e) => {
  const raw = _sdxRaw(e);
  if (Array.isArray(raw)) return readSdxValue(raw[0]);
  if (raw && typeof raw === 'object') return raw.value ?? raw.label ?? '';
  return String(raw ?? '');
};

// Liste (string[]) – für multiple Selects
export const readSdxValueList = (e) => {
  const raw = _sdxRaw(e);
  const arr = Array.isArray(raw) ? raw : [raw];
  return arr
    .map((x) => (x && typeof x === 'object' ? (x.value ?? x.label ?? '') : String(x ?? '')))
    .filter((v) => v !== '');
};

// Allowed units per energy/fuel selection

const FUEL_UNITS_BY_TYPE = {
  Diesel: ["l"],
  Benzin: ["l"],
  Elektrisch: ["kWh"],
};
const HEATING_UNITS_BY_CARRIER = {
  Öl: ["l"],
  Gas: ["kWh", "m3"],
  Holz: ["kg"],
  Fernwärme: ["kWh"],

};
const FALLBACK_FUEL_UNITS = ["l", "kWh"];
const FALLBACK_HEATING_UNITS = ["l", "kg", "kWh", "m3"];
const getAllowedFuelUnits = (type) => FUEL_UNITS_BY_TYPE[type] ?? FALLBACK_FUEL_UNITS;
const getAllowedHeatingUnits = (carrier) => HEATING_UNITS_BY_CARRIER[carrier] ?? FALLBACK_HEATING_UNITS;
const ensureValidUnit = (units, currentUnit) =>
  units.includes(currentUnit) ? currentUnit : (units[0] ?? "");

/* --- CSV utilities --- */
function csvEscape(value) {
  const v = value == null ? '' : String(value);
  if (/[",\n;]/.test(v)) return '"' + v.replace(/"/g, '""') + '"';
  return v;
}

/* Fuel row */
function FuelRow({ row, allowedUnits = FALLBACK_FUEL_UNITS, touched, onTouch, onTypeChange, onUnitChange, onAmountChange, onRemove }) {
  const unitOptions = allowedUnits.length ? allowedUnits : FALLBACK_FUEL_UNITS;

  // Oben in der Datei, bei den anderen Helpers (ausserhalb der Komponente)
const chFormatter = new Intl.NumberFormat('de-CH', {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
});

function formatCH(value) {
  if (value == null || value === "") return "-";
  const num = Number(value);
  if (isNaN(num)) return "-";
  return chFormatter.format(num).replace(/[\u2019\u202F\u00A0]/g, "'");
}

  return (
    <tr>
      <td>
        <sdx-select
          placeholder="Auswählen…"
          onInput={(e) => {
            onTypeChange(readSdxValue(e))
            onTouch();
          }}
          valid={touched && !row.type ? "false" : undefined}
        //validation-message={touched && !row.type ? "Pflichtfeld" : undefined}
        >
          <sdx-select-option value="Diesel" selected={row.type === "Diesel"}>Diesel</sdx-select-option>
          <sdx-select-option value="Benzin" selected={row.type === "Benzin"}>Benzin</sdx-select-option>
          <sdx-select-option value="Elektrisch" selected={row.type === "Elektrisch"}>Elektrisch</sdx-select-option>
        </sdx-select>
      </td>
      <td>
        <sdx-select
          placeholder="Auswählen…"
          onInput={(e) => {
            onUnitChange(readSdxValue(e))
            onTouch();
          }}
          valid={touched && !row.unit ? "false" : undefined}
        //validation-message={touched && !row.unit ? "Pflichtfeld" : undefined}
        >
          <sdx-select-option value="l" selected={row.unit === "l"} disabled={!unitOptions.includes("l")}>l</sdx-select-option>
          <sdx-select-option value="kWh" selected={row.unit === "kWh"} disabled={!unitOptions.includes("kWh")}>kWh</sdx-select-option>
        </sdx-select>
      </td>
      <td>
        <sdx-input
          type="text"
          inputmode="decimal"
          placeholder="Menge"
          value={formatNumericInput(row.amount ?? "")}
          onInput={(e) => {
            onAmountChange(cleanNumericInput(e.target.value));
            onTouch();
          }}
          valid={touched && !row.amount ? "false" : undefined}
        //validation-message={touched && !row.amount ? "Pflichtfeld" : undefined}
        ></sdx-input>
      </td>
      <td className="text-align-right">
        <sdx-button theme="cancel" variant="ghost" label="Löschen" onClick={onRemove}></sdx-button>
      </td>
    </tr>
  );
}

/* Heating row */
function HeatingRow({ row, allowedUnits = FALLBACK_HEATING_UNITS, touched, onTouch, onCarrierChange, onAreaChange, onUnitChange, onAmountChange, onRemove }) {
  const unitOptions = allowedUnits.length ? allowedUnits : FALLBACK_HEATING_UNITS;
  const isWärmepumpe = row.carrier === "Wärmepumpe";

  return (
    <tr>
      <td>
        <sdx-select
          placeholder="Auswählen…"
          onInput={(e) => {
            onAreaChange(readSdxValue(e));
            onTouch();
          }}
          valid={touched && !row.area ? "false" : undefined}
        //validation-message={touched && !row.area ? "Pflichtfeld" : undefined}
        >
          <sdx-select-option value="Heizung" selected={row.area === "Heizung"}>Heizung</sdx-select-option>
          <sdx-select-option value="Lackierkabinen" selected={row.area === "Lackierkabinen"}>Lackierkabinen</sdx-select-option>
        </sdx-select>
      </td>
      <td>
        <sdx-select
          placeholder="Auswählen…"
          onInput={(e) => {
            onCarrierChange(readSdxValue(e));
            onTouch();
          }}
          valid={touched && !row.carrier ? "false" : undefined}
        //validation-message={touched && !row.carrier ? "Pflichtfeld" : undefined}
        >
          <sdx-select-option value="Öl" selected={row.carrier === "Öl"}>Öl</sdx-select-option>
          <sdx-select-option value="Gas" selected={row.carrier === "Gas"}>Gas</sdx-select-option>
          <sdx-select-option value="Holz" selected={row.carrier === "Holz"}>Holz</sdx-select-option>
          <sdx-select-option value="Fernwärme" selected={row.carrier === "Fernwärme"}>Fernwärme</sdx-select-option>
          <sdx-select-option value="Wärmepumpe" selected={row.carrier === "Wärmepumpe"}>Wärmepumpe</sdx-select-option>
        </sdx-select>
      </td>
      <td>
        <sdx-select
          placeholder="Auswählen…"
          disabled={isWärmepumpe}
          onInput={(e) => {
            onUnitChange(readSdxValue(e));
            onTouch();
          }}
          valid={touched && !row.unit && !isWärmepumpe ? "false" : undefined}
        //validation-message={touched && !row.unit && !isWärmepumpe ? "Pflichtfeld" : undefined}
        >
          <sdx-select-option value="l" selected={row.unit === "l"} disabled={!unitOptions.includes("l")}>l</sdx-select-option>
          <sdx-select-option value="kg" selected={row.unit === "kg"} disabled={!unitOptions.includes("kg")}>kg</sdx-select-option>
          <sdx-select-option value="kWh" selected={row.unit === "kWh"} disabled={!unitOptions.includes("kWh")}>kWh</sdx-select-option>
          <sdx-select-option value="m3" selected={row.unit === "m3"} disabled={!unitOptions.includes("m3")}>m3</sdx-select-option>
        </sdx-select>
      </td>
      <td>
        <sdx-input
          type="text"
          inputmode="decimal"
          placeholder="Menge"
          disabled={isWärmepumpe}
          value={formatNumericInput(row.amount ?? "")}
          onInput={(e) => {
            onAmountChange(cleanNumericInput(e.target.value));
            onTouch();
          }}
          valid={touched && !row.amount && !isWärmepumpe ? "false" : undefined}
        //validation-message={touched && !row.amount && !isWärmepumpe ? "Pflichtfeld" : undefined}
        ></sdx-input>
      </td>
      <td className="text-align-right">
        <sdx-button theme="cancel" variant="ghost" label="Löschen" onClick={onRemove}></sdx-button>
      </td>
    </tr>
  );
}

function LackierkabinenRow({ row, onCarrierChange, onAreaChange, onUnitChange, onAmountChange, onRemove }) {
  return (
    <tr>
      <td>
        <sdx-select
          placeholder="Art auswählen…"
          onInput={(e) => onAreaChange(readSdxValue(e))}
        >
          <sdx-select-option
            value="Alte Einbrennkabinen mit Gasbrenner und veralteter Steuertechnik"
            selected={row.area === "Alte Einbrennkabinen mit Gasbrenner und veralteter Steuertechnik"}
          >
            Alte Einbrennkabinen mit Gasbrenner und veralteter Steuertechnik
          </sdx-select-option>

          <sdx-select-option
            value="Moderne Kabinen mit Wärmererückgewinnung"
            selected={row.area === "Moderne Kabinen mit Wärmererückgewinnung"}
          >
            Moderne Kabinen mit Wärmererückgewinnung
          </sdx-select-option>

          <sdx-select-option
            value="Moderne Kabinen mit Gasbrenner"
            selected={row.area === "Moderne Kabinen mit Gasbrenner"}
          >
            Moderne Kabinen mit Gasbrenner
          </sdx-select-option>

          <sdx-select-option
            value="Moderne Kabinen mit Gas- und Infrarot-Technik fix in den Wänden verbaut"
            selected={row.area === "Moderne Kabinen mit Gas- und Infrarot-Technik fix in den Wänden verbaut"}
          >
            Moderne Kabinen mit Gas- und Infrarot-Technik fix in den Wänden verbaut
          </sdx-select-option>

          <sdx-select-option
            value="Moderne Kabinen mit Elektroheizung"
            selected={row.area === "Moderne Kabinen mit Elektroheizung"}
          >
            Moderne Kabinen mit Elektroheizung
          </sdx-select-option>

          <sdx-select-option
            value="Moderne Kabinen (Vorbereitung) mit beweglichen UV/Infrarot Armausleger"
            selected={row.area === "Moderne Kabinen (Vorbereitung) mit beweglichen UV/Infrarot Armausleger"}
          >
            Moderne Kabinen (Vorbereitung) mit beweglichen UV/Infrarot Armausleger
          </sdx-select-option>

          <sdx-select-option
            value="Grosse und kleine Kabinen"
            selected={row.area === "Grosse und kleine Kabinen"}
          >
            Grosse und kleine Kabinen
          </sdx-select-option>

          <sdx-select-option
            value="Kombikabinen mit Trennwänden (teils nur Vorbereitung, teils mit Bodenschienen, um das Fahrzeug von der Vorbereitungskabine in die Lackierkabine zu schieben)"
            selected={row.area === "Kombikabinen mit Trennwänden (teils nur Vorbereitung, teils mit Bodenschienen, um das Fahrzeug von der Vorbereitungskabine in die Lackierkabine zu schieben)"}
          >
            Kombikabinen mit Trennwänden (teils nur Vorbereitung, teils mit Bodenschienen, um das Fahrzeug von der Vorbereitungskabine in die Lackierkabine zu schieben)
          </sdx-select-option>

        </sdx-select>
      </td>
      <td className="text-align-right">
        <sdx-button theme="cancel" variant="ghost" label="Löschen" onClick={onRemove}></sdx-button>
      </td>
    </tr>
  );
}

function FossilRow({ row, onTypeChange, onAmountChange, onRemove }) {
  return (
    <tr>
      <td>
        <sdx-select
          placeholder="Treibstofftyp auswählen"
          onInput={(e) => onTypeChange(readSdxValue(e))}
        >
          <sdx-select-option value="Diesel" selected={row.type === "Diesel"}>Diesel</sdx-select-option>
          <sdx-select-option value="Benzin" selected={row.type === "Benzin"}>Benzin</sdx-select-option>
        </sdx-select>
      </td>
      <td>
        <sdx-input
          type="text"
          inputmode="decimal"
          placeholder="Anzahl"
          value={formatNumericInput(row.amount ?? "")}
          onInput={(e) => onAmountChange(cleanNumericInput(e.target.value))}
        ></sdx-input>
      </td>
      <td className="text-align-right">
        <sdx-button theme="cancel" variant="ghost" label="Löschen" onClick={onRemove}></sdx-button>
      </td>
    </tr>
  );
}

function LightRow({ row, onTypeChange, onAmountChange, onRemove }) {
  return (
    <tr>
      <td>
        <sdx-select
          placeholder="Beleuchtungstyp auswählen"
          onInput={(e) => onTypeChange(readSdxValue(e))}
        >
          <sdx-select-option value="LED" selected={row.type === "LED"}>LED</sdx-select-option>
          <sdx-select-option value="Halogen" selected={row.type === "Halogen"}>Halogen</sdx-select-option>
          <sdx-select-option value="Glühlampe" selected={row.type === "Glühlampe"}>Glühlampe</sdx-select-option>
        </sdx-select>
      </td>
      <td>
        <sdx-input
          type="text"
          inputmode="decimal"
          placeholder="Anzahl"
          value={formatNumericInput(row.amount ?? "")}
          onInput={(e) => onAmountChange(cleanNumericInput(e.target.value))}
        ></sdx-input>
      </td>
      <td className="text-align-right">
        <sdx-button theme="cancel" variant="ghost" label="Löschen" onClick={onRemove}></sdx-button>
      </td>
    </tr>
  );
}

/* Review screen */
function ReviewScreen({ userId, company, data, onBackToForm, onSaved }) {
  const { formData, heatingRows, fuelRows, detailsData, lackierkabinenRows, fossilRows, lightRows } = data;
  const saveTimeoutRef = useRef(null);
  const [isSaving, setIsSaving] = useState(false);
  const reviewSectionStyle = {
    marginBottom: "1.5rem",
    padding: 16,
    background: "#fff",
    border: `1px solid ${SOFT_BORDER}`,
    borderRadius: 12,
  };

  const reviewHeadingStyle = {
    color: BRAND,
    marginBottom: "0.75rem"
  };

  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);

  const uploadCSVToS3 = async () => {
    if (isSaving) return;

    setIsSaving(true);
    showProcessingNotification({
      message:
        "Daten werden gespeichert und anschliessend für das Dashboard verarbeitet.",
    });

    try {
      const csv = buildCSV({
        userId,
        company,
        data: {
          ...formData,
          heizung: heatingRows,
          treibstoff: fuelRows,
          details: detailsData,
          lackierkabinen: lackierkabinenRows,
          fossileFahrzeuge: fossilRows,
          beleuchtung: lightRows,
        }
      });

      const BOM = "\uFEFF";
      const blob = new Blob([BOM, csv], { type: "text/csv;charset=utf-8;" });
      const year = formData.jahr || new Date().getFullYear();
      const timestamp = new Date().toISOString().replace(/[:.]/g, "-");
      const filename = `eingaben_${year}_${company}_${timestamp}.csv`;

      const res = await fetch(
        `https://3h7d6zdnlo6xp4l2shhw4zx6540eaeoo.lambda-url.eu-west-1.on.aws/?filename=${encodeURIComponent(
          filename
        )}&folder=eingabemaske`
      );
      if (!res.ok) {
        throw new Error(`Signierte Upload-URL konnte nicht geladen werden (${res.status})`);
      }

      const { uploadUrl } = await res.json();
      if (!uploadUrl) {
        throw new Error("Upload-URL fehlt in der Serverantwort");
      }

      const uploadRes = await fetch(uploadUrl, {
        method: "PUT",
        body: blob,
        headers: { "Content-Type": "text/csv" }
      });
      if (!uploadRes.ok) {
        throw new Error(`CSV-Upload fehlgeschlagen (${uploadRes.status})`);
      }
      if (typeof onSaved === "function") {
        onSaved(formData);
      } else {
        showProcessingSuccessNotification({
          message: "CSV erfolgreich gespeichert.",
        });
        setIsSaving(false);
      }
    } catch (error) {
      console.error("uploadCSVToS3 failed:", error);
      const message =
        error instanceof Error ? error.message : "Unbekannter Fehler beim Speichern";
      showProcessingErrorNotification({
        message: `Speichern fehlgeschlagen: ${message}`,
      });
      setIsSaving(false);
    }
  };

  const MetaItem = ({ label, value }) =>
    value == null || value === "" ? null : (
      <div className="meta-item" style={{ display: "flex", gap: "0.5rem", marginBottom: "0.25rem" }}>
        <strong style={{ minWidth: "12rem" }}>{label}:</strong>
        <span>{Array.isArray(value) ? value.join(", ") : String(value)}</span>
      </div>
    );

  return (

    <div className="sdx greenone-data-collection" style={{ maxWidth: "900px", margin: "0 auto", padding: "2rem" }}>

      <h2
        className="h2"
        style={{
          marginBottom: "1rem",
          color: BRAND
        }}
      >
        Übersicht Ihrer Eingaben
      </h2>
      <p style={{ marginTop: 0, marginBottom: "1rem" }}>
        Unternehmen: <strong>{company}</strong>
      </p>

      <section style={reviewSectionStyle}>
        <h3 className="h3" style={reviewHeadingStyle}>Allgemein</h3>
        <div style={{ display: "flex", gap: "0.5rem", marginBottom: "0.25rem" }}>
          <strong style={{ minWidth: "12rem" }}>Bereiche:</strong>
          <span>{Array.isArray(formData.bereiche) ? formData.bereiche.join(", ") : formData.bereiche || "-"}</span>
        </div>
        <div style={{ display: "flex", gap: "0.5rem", marginBottom: "0.25rem" }}>
          <strong style={{ minWidth: "12rem" }}>Jahr:</strong>
          <span>{formData.jahr || "-"}</span>
        </div>
        <div style={{ display: "flex", gap: "0.5rem", marginBottom: "0.25rem" }}>
          <strong style={{ minWidth: "12rem" }}>Mitarbeitende:</strong>
          <span>{formData.mitarbeitende || "-"}</span>
        </div>
        <div style={{ display: "flex", gap: "0.5rem", marginBottom: "0.25rem" }}>
          <strong style={{ minWidth: "12rem" }}>Produktive Mitarbeitende:</strong>
          <span>{formData.produktiveMitarbeitende || "-"}</span>
        </div>
        <div style={{ display: "flex", gap: "0.5rem", marginBottom: "0.25rem" }}>
          <strong style={{ minWidth: "12rem" }}>Anzahl Reparaturen:</strong>
          <span>{formData.reparaturen || "-"}</span>
        </div>
        <div style={{ display: "flex", gap: "0.5rem", marginBottom: "0.25rem" }}>
          <strong style={{ minWidth: "12rem" }}>Verkaufte Stunden:</strong>
          <span>{Number(formData.stunden)?.toLocaleString('de-CH') || "-"}</span>
        </div>
        <div style={{ display: "flex", gap: "0.5rem", marginBottom: "0.25rem" }}>
          <strong style={{ minWidth: "12rem" }}>Betriebsgrösse (m²):</strong>
          <span>{formData.betriebsgrösse || "-"}</span>
        </div>
      </section>

      <section style={reviewSectionStyle}>
        <h3 className="h3" style={reviewHeadingStyle}>Strom</h3>
        <div style={{ display: "flex", gap: "0.5rem", marginBottom: "0.25rem" }}>
          <strong style={{ minWidth: "12rem" }}>Strommix:</strong>
          <span>{formData.strommix || "-"}</span>
        </div>
        {formData.strommix === "x% erneuerbar" && (
          <div style={{ display: "flex", gap: "0.5rem", marginBottom: "0.25rem" }}>
            <strong style={{ minWidth: "12rem" }}>Anteil erneuerbar (%):</strong>
            <span>{formData.renewableShare ?? "-"}%</span>
          </div>
        )}
        <div style={{ display: "flex", gap: "0.5rem", marginBottom: "0.25rem" }}>
          <strong style={{ minWidth: "12rem" }}>Menge in kWh:</strong>
          <span>{formData.mengeStrom || "-"}</span>
        </div>
        <div style={{ display: "flex", gap: "0.5rem", marginBottom: "0.25rem" }}>
          <strong style={{ minWidth: "12rem" }}>Eigene PV Anlage:</strong>
          <span>{formData.pvOwn || "-"}</span>
        </div>
        {formData.pvOwn === "Ja" && (
          <div style={{ display: "flex", gap: "0.5rem", marginBottom: "0.25rem" }}>
            <strong style={{ minWidth: "12rem" }}>PV-Menge (kWh):</strong>
            <span>{formData.pvMengeKwh || "-"}</span>
          </div>
        )}
      </section>

      <section style={reviewSectionStyle}>
        <h3 className="h3" style={reviewHeadingStyle}>Lack</h3>
        <div style={{ display: "flex", gap: "0.5rem", marginBottom: "0.25rem" }}>
          <strong style={{ minWidth: "12rem" }}>Lackmix:</strong>
          <span>{formData.lackmix || "-"}</span>
        </div>
        {formData.lackmix === "x% lufttrocknender Lack" && (
          <div style={{ display: "flex", gap: "0.5rem", marginBottom: "0.25rem" }}>
            <strong style={{ minWidth: "12rem" }}>Anteil lufttrocknend (%):</strong>
            <span>{formData.lufttrocknendShare ?? "-"}</span>
          </div>
        )}
      </section>

      <section style={reviewSectionStyle}>
        <h3 className="h3" style={reviewHeadingStyle}>Heizung</h3>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead style={{ color: BRAND }}>
            <tr style={{ borderBottom: `1px solid ${SOFT_BORDER}` }}>
              <th style={{ textAlign: "left", padding: "4px 8px" }}>Bereich</th>
              <th style={{ textAlign: "left", padding: "4px 8px" }}>Energieträger</th>
              <th style={{ textAlign: "left", padding: "4px 8px" }}>Einheit</th>
              <th style={{ textAlign: "left", padding: "4px 8px" }}>Menge</th>
            </tr>
          </thead>
          <tbody>
            {heatingRows.map((r, i) => (
              <tr key={r.id ?? i} style={{ borderBottom: "1px solid #f1f5f9" }}>
                <td style={{ padding: "4px 8px" }}>{r.area || "-"}</td>
                <td style={{ padding: "4px 8px" }}>{r.carrier || "-"}</td>
                <td style={{ padding: "4px 8px" }}>{r.unit || "-"}</td>
                <td style={{ padding: "4px 8px" }}>{r.amount || "-"}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <div style={{ marginTop: "1rem" }}>
          <div style={{ display: "flex", gap: "0.5rem", marginBottom: "0.25rem" }}>
            <strong style={{ minWidth: "12rem" }}>Wärmepumpe:</strong>
            <span>{formData.wärmepumpe ?? "-"}</span>
          </div>
          {formData.wärmepumpe === "Ja" && (
            <div style={{ display: "flex", gap: "0.5rem", marginBottom: "0.25rem" }}>
              <strong style={{ minWidth: "12rem" }}>Bereich:</strong>
              <span>{formData.wärmepumpeBereiche ?? "-"}</span>
            </div>
          )}
        </div>
      </section>

      <section style={reviewSectionStyle}>
        <h3 className="h3" style={reviewHeadingStyle}>Treibstoff / Elektrische Fahrzeuge</h3>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead style={{ color: BRAND }}>
            <tr style={{ borderBottom: `1px solid ${SOFT_BORDER}` }}>
              <th style={{ textAlign: "left", padding: "4px 8px" }}>Treibstofftyp</th>
              <th style={{ textAlign: "left", padding: "4px 8px" }}>Einheit</th>
              <th style={{ textAlign: "left", padding: "4px 8px" }}>Menge</th>
            </tr>
          </thead>
          <tbody>
            {fuelRows.map((r, i) => (
              <tr key={r.id ?? i} style={{ borderBottom: "1px solid #f1f5f9" }}>
                <td style={{ padding: "4px 8px" }}>{r.type || "-"}</td>
                <td style={{ padding: "4px 8px" }}>{r.unit || "-"}</td>
                <td style={{ padding: "4px 8px" }}>{r.amount || "-"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <section style={reviewSectionStyle}>
        <h3 className="h3" style={reviewHeadingStyle}>Einkauf</h3>
        <MetaItem label="Lack (CHF)" value={formData.verbrauchsmaterialLack} />
        <MetaItem label="Verbrauchsmaterialien (CHF)" value={formData.verbrauchsmaterialAllg} />
        <MetaItem label="Ersatzteile (CHF)" value={formData.verbrauchsmaterialErsatzteile} />
      </section>

      {/* NEU: Lackierkabinen als Tabelle */}
      <section style={reviewSectionStyle}>
        <h3 className="h3" style={reviewHeadingStyle}>Lackierkabinen</h3>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead style={{ color: BRAND }}>
            <tr style={{ borderBottom: `1px solid ${SOFT_BORDER}` }}>
              <th style={{ textAlign: "left", padding: "4px 8px" }}>Art</th>
            </tr>
          </thead>
          <tbody>
            {lackierkabinenRows?.map((r, i) => (
              <tr key={r.id ?? i} style={{ borderBottom: "1px solid #f1f5f9" }}>
                <td style={{ padding: "4px 8px" }}>{r.area || "-"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <section style={reviewSectionStyle}>
        <h3 className="h3" style={reviewHeadingStyle}>Abfall</h3>
        <MetaItem label="Menge in Tonnen" value={detailsData.abfallTonnen} />
      </section>

      <section style={reviewSectionStyle}>
        <h3 className="h3" style={reviewHeadingStyle}>Betriebsgrösse</h3>
        <MetaItem label="Fläche in m²" value={detailsData.betriebsgrösse} />
      </section>

      <section style={reviewSectionStyle}>
        <h3 className="h3" style={reviewHeadingStyle}>Lack</h3>
        <MetaItem label="Lackmix" value={detailsData?.lackmix} />
        {detailsData?.lackmix === "x% lufttrocknender Lack" && (
          <MetaItem label="Anteil lufttrocknender Lack (%)" value={detailsData?.lufttrocknendShare} />
        )}
      </section>

      <section style={reviewSectionStyle}>
        <h3 className="h3" style={reviewHeadingStyle}>Anzahl Fossile Fahrzeuge</h3>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead style={{ color: BRAND }}>
            <tr style={{ borderBottom: `1px solid ${SOFT_BORDER}` }}>
              <th style={{ textAlign: "left", padding: "4px 8px" }}>Treibstofftyp</th>
              <th style={{ textAlign: "left", padding: "4px 8px" }}>Anzahl</th>
            </tr>
          </thead>
          <tbody>
            {fossilRows?.map((r, i) => (
              <tr key={r.id ?? i} style={{ borderBottom: "1px solid #f1f5f9" }}>
                <td style={{ padding: "4px 8px" }}>{r.type || "-"}</td>
                <td style={{ padding: "4px 8px" }}>{r.amount || "-"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <section style={reviewSectionStyle}>
        <h3 className="h3" style={reviewHeadingStyle}>Art der Beleuchtung</h3>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead style={{ color: BRAND }}>
            <tr style={{ borderBottom: `1px solid ${SOFT_BORDER}` }}>
              <th style={{ textAlign: "left", padding: "4px 8px" }}>Beleuchtungstyp</th>
              <th style={{ textAlign: "left", padding: "4px 8px" }}>Anzahl</th>
            </tr>
          </thead>
          <tbody>
            {lightRows?.map((r, i) => (
              <tr key={r.id ?? i} style={{ borderBottom: "1px solid #f1f5f9" }}>
                <td style={{ padding: "4px 8px" }}>{r.type || "-"}</td>
                <td style={{ padding: "4px 8px" }}>{r.amount || "-"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <section style={reviewSectionStyle}>
        <h3 className="h3" style={reviewHeadingStyle}>Einkauf & Abfall</h3>
        <div style={{ display: "flex", gap: "0.5rem", marginBottom: "0.25rem" }}>
          <strong style={{ minWidth: "12rem" }}>Lack (CHF):</strong>
          <span>{formData.verbrauchsmaterialLack || "-"}</span>
        </div>
        <div style={{ display: "flex", gap: "0.5rem", marginBottom: "0.25rem" }}>
          <strong style={{ minWidth: "12rem" }}>Verbrauchsmaterialien (CHF):</strong>
          <span>{formData.verbrauchsmaterialAllg || "-"}</span>
        </div>
        <div style={{ display: "flex", gap: "0.5rem", marginBottom: "0.25rem" }}>
          <strong style={{ minWidth: "12rem" }}>Ersatzteile (CHF):</strong>
          <span>{formData.verbrauchsmaterialErsatzteile || "-"}</span>
        </div>
        <div style={{ display: "flex", gap: "0.5rem", marginBottom: "0.25rem" }}>
          <strong style={{ minWidth: "12rem" }}>Abfall (t/Jahr):</strong>
          <span>{formData.abfallTonnen || "-"}</span>
        </div>
      </section>

      <div style={{ display: "flex", gap: "0.75rem", marginTop: "1.5rem" }}>
        <sdx-button
          variant="primary"
          label={isSaving ? "Speichern..." : "Speichern"}
          disabled={isSaving}
          onClick={uploadCSVToS3}
        ></sdx-button>
        <sdx-button
          theme="secondary"
          label="Zur Bearbeitung zurück"
          disabled={isSaving}
          onClick={onBackToForm}
        ></sdx-button>
      </div>
    </div>
  )
};


function DetailScreen({
  detailsData, setDetailsData,
  lackierkabinenRows, lackierkabinenRowHandlers, onAddLackierkabinenRow,
  fossilRows, fossilRowHandlers, onAddFossilRow,
  lightRows, lightRowHandlers, onAddLightRow,
  onNext, onBack
}) {

  const handleChange = (field, value) =>
    setDetailsData(prev => ({ ...prev, [field]: value }));

  useEffect(() => {
    if (detailsData.lackmix !== 'x% lufttrocknender Lack' && 'lufttrocknendShare' in detailsData) {
      setDetailsData(({ lufttrocknendShare, ...rest }) => rest);
    }
  }, [detailsData.lackmix]);

  return (
    <div className="sdx greenone-data-collection" style={{ maxWidth: "900px", margin: "0 auto", padding: "2rem" }}>
      <h2 className="h2" style={{ color: BRAND }}>
        Weitere Details
      </h2>

      {/* Lackierkabinen als Mehrzeilen-Tabelle */}
      <h3
        className="h3"
        style={{
          marginTop: "1.5rem",
          color: BRAND
        }}
      >
        Lackierkabinen
      </h3>
      <div className="table table--responsive">
        <div className="table__wrapper">
          <table>
            <thead style={{ color: BRAND }}>
              <tr>
                <th>Art</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {lackierkabinenRows.map((row) => {
                const h = lackierkabinenRowHandlers[row.id];
                return (
                  <LackierkabinenRow
                    key={row.id}
                    row={row}
                    onCarrierChange={h.onCarrierChange}
                    onAreaChange={h.onAreaChange}
                    onRemove={h.onRemove}
                  />
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      <sdx-button theme="secondary" onClick={onAddLackierkabinenRow} label="Zeile hinzufügen" style={{ marginTop: '1rem' }}></sdx-button>


      <h3
        className="h3"
        style={{
          marginTop: "2rem",
          color: BRAND
        }}
      >
        Betriebsgrösse
      </h3>

      <sdx-input
        label="Fläche in m²"
        type="text"
        inputmode="decimal"
        value={formatNumericInput(detailsData.betriebsgrösse ?? "")}
        onInput={(e) => handleChange('betriebsgrösse', cleanNumericInput(e.target.value))}
      ></sdx-input>

      <h3
        className="h3"
        style={{
          marginTop: "1.5rem",
          color: BRAND
        }}
      >
        Anzahl Fossile Fahrzeuge
      </h3>
      <div className="table table--responsive">
        <div className="table__wrapper">
          <table>
            <thead style={{ color: BRAND }}>
              <tr>
                <th>Treibstofftyp</th>
                <th>Anzahl</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {fossilRows.map((row) => {
                const h = fossilRowHandlers[row.id];
                return (
                  <FossilRow
                    key={row.id}
                    row={row}
                    onTypeChange={h.onTypeChange}
                    onAmountChange={h.onAmountChange}
                    onRemove={h.onRemove}
                  />
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      <sdx-button theme="secondary" onClick={onAddFossilRow} label="Zeile hinzufügen" style={{ marginTop: '1rem' }}></sdx-button>

      <h3
        className="h3"
        style={{
          marginTop: "1.5rem",
          color: BRAND
        }}
      >
        Art der Beleuchtung
      </h3>
      <div className="table table--responsive">
        <div className="table__wrapper">
          <table>
            <thead style={{ color: BRAND }}>
              <tr>
                <th>Beleuchtungstyp</th>
                <th>Anzahl</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {lightRows.map((row) => {
                const h = lightRowHandlers[row.id];
                return (
                  <LightRow
                    key={row.id}
                    row={row}
                    onTypeChange={h.onTypeChange}
                    onAmountChange={h.onAmountChange}
                    onRemove={h.onRemove}
                  />
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <sdx-button theme="secondary" onClick={onAddLightRow} label="Zeile hinzufügen" style={{ marginTop: '1rem' }}></sdx-button>

      <div style={{ display: "flex", gap: "0.75rem", marginTop: "1.5rem" }}>
        <sdx-button theme="secondary" label="Zurück" onClick={onBack}></sdx-button>
        <sdx-button variant="primary" label="Weiter zur Übersicht" onClick={onNext}></sdx-button>

      </div>
    </div>
  );
}

/* Eingabemaske */
export default function Eingabemaske({ user, onBack, onSaved }) {
  const [step, setStep] = useState('years');
  const [activeYear, setActiveYear] = useState(null);
  const [savedYears, setSavedYears] = useState(() => {
    if (typeof window === 'undefined') return {};
    try {
      const raw = window.localStorage.getItem('eingabemaskeSavedYears');
      return raw ? JSON.parse(raw) : {};
    } catch {
      return {};
    }
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem('eingabemaskeSavedYears', JSON.stringify(savedYears));
    } catch {
    }
  }, [savedYears]);

  useEffect(() => {
    if (!user) return;

    async function loadBackendYears() {
      try {
        const session = await fetchAuthSession();
        const token = session?.tokens?.idToken ? session.tokens.idToken.toString() : null;

        const res = await fetch(
          EMISSIONS_API_URL,
          {
            headers: {
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
              "Content-Type": "application/json"
            }
          }
        );

        if (!res.ok) {
          console.warn('Eingabemaske: backend years request failed', res.status);
          return;
        }

        const data = await res.json();
        let parsed = data;
        if (typeof data.body === "string") parsed = JSON.parse(data.body);
        else if (data.body && typeof data.body === "object") parsed = data.body;

        const rows = Array.isArray(parsed)
          ? parsed
          : Array.isArray(parsed.rows)
            ? parsed.rows
            : [];

        const years = Array.from(new Set(rows.filter((r) => r?.year != null).map((r) => String(r.year))));
        years.sort((a, b) => Number(b) - Number(a));
        setBackendYears(years);
      } catch (err) {
        console.warn("Eingabemaske: Backend years load failed", err);
      }
    }

    loadBackendYears();
  }, [user]);

  const [backendYears, setBackendYears] = useState([]);

  const combinedYears = useMemo(() => {
    const all = [
      ...Object.keys(savedYears || {}).map(String),
      ...((backendYears || []).map(String))
    ];
    return Array.from(new Set(all))
      .filter(year => Number.isInteger(Number(year)) && Number(year) >= 2020)
      .sort((a, b) => Number(a) - Number(b));
  }, [savedYears, backendYears]);

  const [formData, setFormData] = useState({});
  const [mode, setMode] = useState('form');
  const [snapshot, setSnapshot] = useState(null);
  const [detailsData, setDetailsData] = useState({});
  const company = user?.attributes?.["custom:company"];
  const userId = user?.username;
  const [touched, setTouched] = useState({})
  const [touchedRows, setTouchedRows] = useState({});

  useEffect(() => {
    const root = document.querySelector(
      ".greenone-data-collection"
    );

    return applyGreenOneSdxButtonStyles(root);
  }, [step, mode]);

  const isJahrInvalid = () => {
    const val = Number(formData.jahr);
    const currentYear = new Date().getFullYear();
    const invalid = !formData.jahr || !Number.isInteger(val) || val < 2020 || currentYear < val;
    return invalid;
  };
  const isBereicheInvalid = () => {
    return !formData.bereiche || formData.bereiche.length === 0;
  }

  const isRenewableShareInvalid = () => {
    return formData.strommix === 'x% erneuerbar'
      && (formData.renewableShare === undefined || formData.renewableShare === null);
  };

  const showError = touched.renewableShare && isRenewableShareInvalid();

  const isMitarbeitendeInvalid = () => {
    return !formData.mitarbeitende;
  }

  const isProduktiveMitarbeitendeInvalid = () => {
    return !formData.produktiveMitarbeitende;
  }



  const isRowComplete = (row, requiredFields) => {
    if (!row) return false;

    // Sonderfall Wärmepumpe:
    // Verbrauch ist im Strom enthalten → immer gültig
    if (row.carrier === 'Wärmepumpe') {
      return !!row.area && !!row.carrier;
    }

    return requiredFields.every(
      field => row[field] !== undefined && row[field] !== '' && row[field] !== null
    );
  };



  const touchRow = (tableKey, rowId) => {
    setTouchedRows(prev => ({
      ...prev,
      [tableKey]: { ...(prev[tableKey] ?? {}), [rowId]: true }
    }));
  };

  const isRowEmpty = (row, fields) => {
    return fields.every(field => row[field] == null || row[field] === '');
  };

  const areRowsValid = (rows, requiredFields) => {
    if (!rows || rows.length === 0) return true;

    const allEmpty = rows.every(row => isRowEmpty(row, requiredFields));
    if (allEmpty) return true;

    return rows.every(row => isRowComplete(row, requiredFields));
  };

  const isStromValid = () => {
    if (!formData.strommix) return false;
    if (!formData.mengeStrom) return false;
    if (!formData.pvOwn) return false;
    if (formData.pvOwn === 'Ja' && !formData.pvMengeKwh) return false;
    if (formData.strommix === 'x% erneuerbar' && formData.renewableShare == null) return false;
    return true;
  };

  useEffect(() => {
    if (formData.lackmix !== 'x% lufttrocknender Lack' && 'lufttrocknendShare' in formData) {
      setFormData(({ lufttrocknendShare, ...rest }) => rest);
    }
  }, [formData.lackmix]);

  useEffect(() => {
    if (formData.strommix !== 'x% erneuerbar' && 'renewableShare' in formData) {
      setFormData(({ renewableShare, ...rest }) => rest);
    }
  }, [formData.strommix]);

  useEffect(() => {
    if (formData.pvOwn !== 'Ja' && 'pvMengeKwh' in formData) {
      setFormData(({ pvMengeKwh, ...rest }) => rest);
    }
  }, [formData.pvOwn]);

  useEffect(() => {
    if (formData.wärmepumpe !== 'Ja' && 'wärmepumpeBereiche' in formData) {
      setFormData(({ wärmepumpeBereiche, ...rest }) => rest);
    }
  }, [formData.wärmepumpe]);

  const [fuelRows, setFuelRows] = useState([{ id: 1, type: '', unit: '', amount: '' }]);
  const [rowId, setRowId] = useState(2);

  const [heatingRows, setHeatingRows] = useState([
    { id: 1, carrier: '', area: '', unit: '', amount: '' }
  ]);
  const [heatingRowId, setHeatingRowId] = useState(2);

  // NEU: Lackierkabinen als eigene Tabelle (Details)
  const [lackierkabinenRows, setLackierkabinenRows] = useState([
    { id: 1, carrier: '' }
  ]);
  const [lackierkabinenRowId, setLackierkabinenRowId] = useState(2);

  const [fossilRows, setFossilRows] = useState([
    { id: 1, type: '', amount: '' }
  ]);
  const [fossilRowId, setFossilRowId] = useState(2);

  const addFossilRow = () => {
    setFossilRows(prev => [...prev, { id: fossilRowId, type: '', amount: '' }]);
    setFossilRowId(n => n + 1);
  };

  const updateFossilRow = (id, patch) => {
    setFossilRows(prev => prev.map(r => r.id === id ? { ...r, ...patch } : r));
  };
  const removeFossilRow = (id) => setFossilRows(prev => prev.filter(r => r.id !== id));

  const fossilRowHandlers = useMemo(
    () => Object.fromEntries(
      fossilRows.map(r => [r.id, {
        onTypeChange: (val) => updateFossilRow(r.id, { type: val }),
        onAmountChange: (val) => updateFossilRow(r.id, { amount: val }),
        onRemove: () => removeFossilRow(r.id),
      }])
    ),
    [fossilRows]
  );

  const [lightRows, setLightRows] = useState([
    { id: 1, type: '', amount: '' }
  ]);
  const [lightRowId, setLightRowId] = useState(2);

  const addLightRow = () => {
    setLightRows(prev => [...prev, { id: lightRowId, type: '', amount: '' }]);
    setLightRowId(n => n + 1);
  };
  const updateLightRow = (id, patch) => {
    setLightRows(prev => prev.map(r => r.id === id ? { ...r, ...patch } : r));
  };
  const removeLightRow = (id) => setLightRows(prev => prev.filter(r => r.id !== id));

  const lightRowHandlers = useMemo(
    () => Object.fromEntries(
      lightRows.map(r => [r.id, {
        onTypeChange: (val) => updateLightRow(r.id, { type: val }),
        onAmountChange: (val) => updateLightRow(r.id, { amount: val }),
        onRemove: () => removeLightRow(r.id),
      }])
    ),
    [lightRows]
  );

  const handleChange = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));


  const getLastRow = (rows) => {
    if (!Array.isArray(rows) || rows.length === 0) return null;
    return rows[rows.length - 1];
  };


  const markRowTouched = (tableKey, row) => {
    if (!row?.id) return;

    setTouchedRows(prev => ({
      ...prev,
      [tableKey]: {
        ...(prev[tableKey] ?? {}),
        [row.id]: true,
      },
    }));
  };



  const addFuelRow = () => {
    const lastRow = getLastRow(fuelRows);

    if (!lastRow) {
      setFuelRows(prev => [
        ...prev,
        { id: rowId, type: '', unit: '', amount: '' }
      ]);
      setRowId(n => n + 1);
      return;
    }

    if (!isRowComplete(lastRow, ['type', 'unit', 'amount'])) {
      markRowTouched('fuel', lastRow);
      return;
    }

    setFuelRows(prev => [
      ...prev,
      { id: rowId, type: '', unit: '', amount: '' }
    ]);
    setRowId(n => n + 1);
  };
  const updateFuelRow = (id, patch) => {
    setFuelRows(prev => prev.map(r => (r.id === id ? { ...r, ...patch } : r)));
  };
  const removeFuelRow = (id) => setFuelRows(prev => prev.filter(r => r.id !== id));

  const fuelRowHandlers = useMemo(
    () =>
      Object.fromEntries(
        fuelRows.map(r => [
          r.id,
          {
            onTypeChange: (val) => {
              const allowedUnits = getAllowedFuelUnits(val);
              const unit = ensureValidUnit(allowedUnits, r.unit);
              updateFuelRow(r.id, { type: val, unit });
            },
            onUnitChange: (val) => updateFuelRow(r.id, { unit: val }),
            onAmountChange: (val) => updateFuelRow(r.id, { amount: val }),
            onRemove: () => removeFuelRow(r.id),
          }
        ])
      ),
    [fuelRows]
  );

  const addHeatingRow = () => {
    const lastRow = getLastRow(heatingRows);

    if (!lastRow) {
      setHeatingRows(prev => [
        ...prev,
        { id: heatingRowId, carrier: '', area: '', unit: '', amount: '' }
      ]);
      setHeatingRowId(n => n + 1);
      return;
    }

    if (!isRowComplete(lastRow, ['area', 'carrier', 'unit', 'amount'])) {
      markRowTouched('heating', lastRow);
      return;
    }

    setHeatingRows(prev => [
      ...prev,
      { id: heatingRowId, carrier: '', area: '', unit: '', amount: '' }
    ]);
    setHeatingRowId(n => n + 1);
  };


  const updateHeatingRow = (id, patch) => {
    setHeatingRows(prev => prev.map(r => (r.id === id ? { ...r, ...patch } : r)));
  };
  const removeHeatingRow = (id) => setHeatingRows(prev => prev.filter(r => r.id !== id));

  const heatingRowHandlers = useMemo(
    () =>
      Object.fromEntries(
        heatingRows.map(r => [
          r.id,
          {
            onCarrierChange: (val) => {
              const allowedUnits = getAllowedHeatingUnits(val);
              const unit = ensureValidUnit(allowedUnits, r.unit);
              updateHeatingRow(r.id, { carrier: val, unit });
            },
            onAreaChange: (val) => updateHeatingRow(r.id, { area: val }),
            onUnitChange: (val) => updateHeatingRow(r.id, { unit: val }),
            onAmountChange: (val) => updateHeatingRow(r.id, { amount: val }),
            onRemove: () => removeHeatingRow(r.id),
          }
        ])
      ),
    [heatingRows]
  );

  // Handlers für Lackierkabinen (NEU)
  const addLackierkabinenRow = () => {
    setLackierkabinenRows(prev => [
      ...prev,
      { id: lackierkabinenRowId, carrier: '', area: '', unit: '', amount: '' }
    ]);
    setLackierkabinenRowId(n => n + 1);
  };
  const updateLackierkabinenRow = (id, patch) => {
    setLackierkabinenRows(prev => prev.map(r => (r.id === id ? { ...r, ...patch } : r)));
  };
  const removeLackierkabinenRow = (id) => setLackierkabinenRows(prev => prev.filter(r => r.id !== id));

  const lackierkabinenRowHandlers = useMemo(
    () =>
      Object.fromEntries(
        lackierkabinenRows.map(r => [
          r.id,
          {
            onCarrierChange: (val) => {
              const allowedUnits = getAllowedFuelUnits(val);
              const unit = ensureValidUnit(allowedUnits, r.unit);
              updateLackierkabinenRow(r.id, { carrier: val })
            },
            onAreaChange: (val) => updateLackierkabinenRow(r.id, { area: val }),

            onRemove: () => removeLackierkabinenRow(r.id),
          }
        ])
      ),
    [lackierkabinenRows]
  );

  function handleYearSelect(year) {
    if (!Number.isInteger(Number(year)) || Number(year) < 2020) return;
    setActiveYear(year);
    setFormData({ jahr: String(year), referenzjahr: String(year) });
    setStep("form");
  }

  function handleAddYear() {
    const nextYear = combinedYears.length > 0
      ? Math.max(...combinedYears.map(Number)) + 1
      : new Date().getFullYear();
    setSavedYears(prev => ({ ...prev, [nextYear]: {} })); // ← neu
    handleYearSelect(nextYear);
  }


  if (mode === 'review' && snapshot) {
    return (
      <ReviewScreen
        userId={userId}
        company={company}
        data={snapshot}
        onBackToForm={() => {
          setFormData(snapshot.formData);
          setHeatingRows(snapshot.heatingRows);
          setFuelRows(snapshot.fuelRows);
          setDetailsData(snapshot.detailsData);
          setLackierkabinenRows(snapshot.lackierkabinenRows);// NEU
          setFossilRows(snapshot.fossilRows);
          setLightRows(snapshot.lightRows);
          setMode('form');
        }}
        onSaved={() => {
          setSavedYears(prev => ({ ...prev, [activeYear]: { _completed: true } }));
          setStep("years");

          if (typeof onSaved === "function") {
            onSaved(formData);
          } else {
            onBack();
          }
        }}
      />
    );
  }


  if (mode === 'details' && snapshot) {
    return (
      <DetailScreen
        detailsData={detailsData}
        setDetailsData={setDetailsData}
        lackierkabinenRows={lackierkabinenRows}
        lackierkabinenRowHandlers={lackierkabinenRowHandlers}
        onAddLackierkabinenRow={addLackierkabinenRow}
        fossilRows={fossilRows}
        fossilRowHandlers={fossilRowHandlers}
        onAddFossilRow={addFossilRow}
        lightRows={lightRows}
        lightRowHandlers={lightRowHandlers}
        onAddLightRow={addLightRow}
        onBack={() => setMode('form')}
        onNext={() => {
          const fossilValid = areRowsValid(fossilRows, ['type', 'amount']);
          const lightValid = areRowsValid(lightRows, ['type', 'amount']);

          if (!fossilValid || !lightValid) {
            setTouchedRows(prev => ({
              ...prev,
              fossil: Object.fromEntries(fossilRows.map(r => [r.id, true])),
              light: Object.fromEntries(lightRows.map(r => [r.id, true])),
            }));
            showAppNotification({ message: "Bitte alle Pflichtfelder ausfüllen.", type: "warning" });
            return;
          }
          setSnapshot({ ...snapshot, detailsData, lackierkabinenRows, fossilRows, lightRows });
          setMode('review');
        }}
      />
    );
  }

  if (step === "years") {
    return (
      <div className="sdx greenone-data-collection" style={{ maxWidth: 1100, margin: "0 auto", padding: "2rem" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 24 }}>
          <sdx-button onClick={onBack} theme="secondary" label="zurück"></sdx-button>
          <h1
            style={{
              margin: 0,
              color: BRAND
            }}
          >
            Eingabemaske
          </h1>
        </div>
        <div style={{ position: "relative", marginBottom: 16 }}>
          <div
            style={{
              width: "100%",
              border: `1px dashed ${SOFT_BORDER}`,
              borderRadius: 12,
              background: "#fff",
              padding: 24
            }}
          >
            <div
              style={{
                fontWeight: 700,
                marginBottom: 4,
                color: BRAND
              }}
            >
              Jahresübersicht
            </div>
            <div style={{ color: MUTED, marginBottom: 20 }}>
              Wähle ein Jahr aus oder füge ein neues hinzu.
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))", gap: 12 }}>
              {combinedYears.map((yearString) => {
                const year = Number(yearString);
                const isBackendYear = backendYears.includes(yearString);
                const isDone = !!savedYears[year]?._completed || isBackendYear;
                const isInProgress = !!savedYears[year]?.snapshot && !isDone;
                const isDisabled = isBackendYear;
                const badgeLabel = isDone ? "Ausgefüllt" : isInProgress ? "In Bearbeitung" : "Neu";

                return (
                  <button
                    key={year}
                    type="button"
                    disabled={isDisabled}
                    onClick={() => {
                      if (!isDisabled) handleYearSelect(year);
                    }}
                    style={{
                      background: isDone
                        ? "#f0fdfa"
                        : isInProgress
                          ? "#FFFBEB"
                          : "#fff",
                      border: isDone
                        ? `1.5px solid ${PRIMARY}`
                        : `1px solid ${SOFT_BORDER}`,
                      borderRadius: 10,
                      padding: "1.25rem 1rem",
                      cursor: isDisabled ? "not-allowed" : "pointer",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: 8,
                      minHeight: 110,
                      justifyContent: "center",
                      textAlign: "center",
                      opacity: isDisabled ? 0.72 : 1
                    }}
                  >

                    <span style={{ fontSize: 22, color: isDone ? PRIMARY : MUTED }}>{isDone ? "✓" : isInProgress ? "✎" : "+"}</span>
                    <span style={{ fontSize: 20, fontWeight: 600, color: isDone ? BRAND : TEXT }}>{year}</span>
                    <span
                      style={{
                        fontSize: 11,
                        padding: "2px 10px",
                        borderRadius: 99,
                        background: isDone
                          ? GREEN_SURFACE
                          : isInProgress
                            ? "#FEF3C7"
                            : "#f1f5f9",
                        color: isDone
                          ? BRAND
                          : isInProgress
                            ? "#92400E"
                            : MUTED,
                        fontWeight: 600
                      }}
                    >
                      {badgeLabel}
                    </span>
                  </button>

                );
              })}

              <button
                type="button"
                onClick={handleAddYear}
                style={{
                  background: "#fff",
                  border: `1px dashed ${INTERACTION}`,
                  borderRadius: 10,
                  padding: "1.25rem 1rem",
                  cursor: "pointer",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 8,
                  minHeight: 110,
                  justifyContent: "center"
                }}
              >
                <span style={{ fontSize: 28, color: INTERACTION }}>+</span>
                <span style={{ fontSize: 13, color: INTERACTION, fontWeight: 600 }}>
                  Jahr hinzufügen
                </span>
              </button>

            </div>
          </div>
        </div>
      </div >
    );
  }

  return (
    <div className="sdx greenone-data-collection" style={{ maxWidth: '900px', margin: '0 auto', padding: '2rem' }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 24 }}>
        <sdx-button onClick={onBack} theme="secondary" label="zurück"></sdx-button>
        <h1
          style={{
            margin: 0,
            color: BRAND
          }}
        >
          Eingabemaske
        </h1>
      </div>

      <sdx-select
        multiple
        label="Welche Bereiche treffen zu?"
        placeholder="Auswählen…"
        onInput={(e) => {
          handleChange('bereiche', readSdxValueList(e));
          setTouched(prev => ({ ...prev, bereiche: true }));
        }}
        valid={touched.bereiche && isBereicheInvalid() ? 'false' : undefined}
        validation-message={
          touched.bereiche && isBereicheInvalid() ? 'Bitte mindestens einen Bereich auswählen' : undefined
        }
        style={{ marginBottom: '1rem' }}
        required
      >
        <sdx-select-option value="Karosserie und Lackbetrieb" selected={formData.bereiche?.includes("Karosserie und Lackbetrieb")}>Carrosserie und Lackbetrieb</sdx-select-option>
        <sdx-select-option value="Tanken" selected={formData.bereiche?.includes("Tanken")}>Tanken</sdx-select-option>
        <sdx-select-option value="Waschen" selected={formData.bereiche?.includes("Waschen")}>Waschen</sdx-select-option>
      </sdx-select>

      <sdx-input
        label="Jahr"
        type="text"
        inputmode="numeric"
        value={formatNumericInput(formData.jahr ?? "")}
        onInput={(e) => {
          const val = cleanNumericInput(e.target.value);
          handleChange("jahr", val);
          handleChange("referenzjahr", val);
        }}
        onBlur={() => setTouched(prev => ({ ...prev, jahr: true }))}
        valid={touched.jahr && isJahrInvalid() ? "false" : undefined}
        validation-message={
          touched.jahr && isJahrInvalid()
            ? `Gültiges Jahr zwischen 2020 und ${new Date().getFullYear()} eingeben`
            : undefined
        }
        required
      ></sdx-input>
      <sdx-input
        label="Anzahl Reparaturen"
        type="text"
        inputmode="decimal"
        value={formatNumericInput(formData.reparaturen ?? "")}
        onInput={(e) => handleChange('reparaturen', cleanNumericInput(e.target.value))}
      ></sdx-input>
      <sdx-input
        label="Anzahl Verkaufte Stunden"
        type="text"
        inputmode="decimal"
        value={formatNumericInput(formData.stunden ?? "")}
        onInput={(e) => handleChange('stunden', cleanNumericInput(e.target.value))}
      ></sdx-input>
      <sdx-input
        label="Anzahl Mitarbeitende"
        type="text"
        inputmode="decimal"
        value={formatNumericInput(formData.mitarbeitende ?? "")}
        onInput={(e) => handleChange('mitarbeitende', cleanNumericInput(e.target.value))}
        onBlur={() => setTouched(prev => ({ ...prev, mitarbeitende: true }))}
        valid={touched.mitarbeitende && isMitarbeitendeInvalid() ? "false" : undefined}
        validation-message={
          touched.mitarbeitende && isMitarbeitendeInvalid() ? "Bitte Anzahl Mitarbeitende eintragen" : undefined
        }
        required
      ></sdx-input>
      <sdx-input
        label="Anzahl Produktive-Mitarbeitende"
        type="text"
        inputmode="decimal"
        value={formatNumericInput(formData.produktiveMitarbeitende ?? "")}
        onInput={(e) => handleChange('produktiveMitarbeitende', cleanNumericInput(e.target.value))}
        onBlur={() => setTouched(prev => ({ ...prev, produktiveMitarbeitende: true }))}
        valid={touched.produktiveMitarbeitende && isProduktiveMitarbeitendeInvalid() ? "false" : undefined}
        validation-message={
          touched.produktiveMitarbeitende && isProduktiveMitarbeitendeInvalid() ? "Bitte Anzahl Produktive-Mitarbeitende eintragen" : undefined
        }
        required
      ></sdx-input>

      <h3
        className="h3"
        style={{
          marginTop: "2rem",
          color: BRAND
        }}
      >
        Betriebsgrösse
      </h3>
      <sdx-input
        label="Fläche in m²"
        type="text"
        inputmode="decimal"
        value={formatNumericInput(formData.betriebsgrösse ?? "")}
        onInput={(e) => handleChange('betriebsgrösse', cleanNumericInput(e.target.value))}
      ></sdx-input>

      <h3
        className="h3"
        style={{
          marginTop: "1.5rem",
          color: BRAND
        }}
      >
        Heizung *
      </h3>
      <div className="table table--responsive">
        <div className="table__wrapper">
          <table>
            <thead style={{ color: BRAND }}>
              <tr>
                <th>Bereich</th>
                <th>Energieträger
                  <sdx-menu-flyout>
                    <sdx-menu-flyout-toggle sr-hint="Mehr Infos">
                      <sdx-icon
                        icon-name="icon-information-circle"
                        style={{ "--sdx-icon-color": INTERACTION }}
                      ></sdx-icon>
                    </sdx-menu-flyout-toggle>
                    <sdx-menu-flyout-content style={{ width: '300px' }}>
                      Der Verbrauch der Wärmepumpe ist im Punkt Strom inkludiert.
                    </sdx-menu-flyout-content>
                  </sdx-menu-flyout>
                </th>
                <th>Einheit</th>
                <th>Menge</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {heatingRows.map((row) => {
                const h = heatingRowHandlers[row.id];
                const allowedUnits = getAllowedHeatingUnits(row.carrier);
                return (
                  <HeatingRow
                    key={row.id}
                    row={row}
                    allowedUnits={allowedUnits}
                    touched={touchedRows?.heating?.[row.id]}
                    onTouch={() => touchRow('heating', row.id)}
                    onCarrierChange={h.onCarrierChange}
                    onAreaChange={h.onAreaChange}
                    onUnitChange={h.onUnitChange}
                    onAmountChange={h.onAmountChange}
                    onRemove={h.onRemove}
                  />
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <sdx-button theme="secondary" onClick={addHeatingRow} label="Zeile hinzufügen" style={{ marginTop: '1rem' }}></sdx-button>

      <h3
        className="h3"
        style={{
          marginTop: "2rem",
          color: BRAND
        }}
      >
        Treibstoff / Elektrische Fahrzeuge *
      </h3>
      <div style={{ marginTop: '1rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
          <label>Besitzen Sie eine Wärmepumpe?</label>
          <sdx-menu-flyout>
            <sdx-menu-flyout-toggle sr-hint="Mehr Infos">
              <sdx-icon
                icon-name="icon-information-circle"
                style={{ "--sdx-icon-color": INTERACTION }}
              ></sdx-icon>
            </sdx-menu-flyout-toggle>
            <sdx-menu-flyout-content style={{ width: '300px' }}>
              Der Verbrauch der Wärmepumpe ist im Punkt Strom inkludiert.
            </sdx-menu-flyout-content>
          </sdx-menu-flyout>
        </div>
        <sdx-select
          placeholder="Auswählen…"
          onInput={(e) => handleChange('wärmepumpe', readSdxValue(e))}
        >
          <sdx-select-option value="Ja" selected={formData.wärmepumpe === "Ja"}>Ja</sdx-select-option>
          <sdx-select-option value="Nein" selected={formData.wärmepumpe === "Nein"}>Nein</sdx-select-option>
        </sdx-select>
      </div>

      {formData.wärmepumpe === 'Ja' && (
        <sdx-select
          label="Für welche Bereiche wird die Wärmepumpe genutzt?"
          placeholder="Auswählen…"
          onInput={(e) => handleChange('wärmepumpeBereiche', readSdxValue(e))}
          style={{ marginTop: '1rem' }}
        >
          <sdx-select-option value="Heizung" selected={formData.wärmepumpeBereiche === "Heizung"}>Heizung</sdx-select-option>
          <sdx-select-option value="Lackierkabine" selected={formData.wärmepumpeBereiche === "Lackierkabine"}>Lackierkabine</sdx-select-option>
          <sdx-select-option value="Beides" selected={formData.wärmepumpeBereiche === "Beides"}>Beides</sdx-select-option>
        </sdx-select>
      )}

      <div className="table table--responsive">
        <div className="table__wrapper">
          <table>
            <thead style={{ color: BRAND }}>
              <tr>
                <th>Treibstofftyp</th>
                <th>Einheit</th>
                <th>Menge</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {fuelRows.map((row) => {
                const h = fuelRowHandlers[row.id];
                const allowedUnits = getAllowedFuelUnits(row.type);
                return (
                  <FuelRow
                    key={row.id}
                    row={row}
                    allowedUnits={allowedUnits}
                    touched={touchedRows?.fuel?.[row.id]}
                    onTouch={() => touchRow('fuel', row.id)}
                    onTypeChange={h.onTypeChange}
                    onUnitChange={h.onUnitChange}
                    onAmountChange={h.onAmountChange}
                    onRemove={h.onRemove}
                  />
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <sdx-button theme="secondary" onClick={addFuelRow} label="Zeile hinzufügen" style={{ marginTop: '1rem' }}></sdx-button>

      <h3
        className="h3"
        style={{
          marginTop: "2rem",
          color: BRAND
        }}
      >
        Strom *
      </h3>
      <sdx-select
        label="Strommix"
        placeholder="Auswählen…"
        onInput={(e) => {
          handleChange('strommix', readSdxValue(e))
          setTouched(prev => ({ ...prev, strommix: true }));
        }}
        valid={touched.strommix && !formData.strommix ? "false" : undefined}
        validation-message={touched.strommix && !formData.strommix ? "Pflichtfeld" : undefined}
      >

        <sdx-select-option value="CH-Mix" selected={formData.strommix === "CH-Mix"}>
          CH-Mix
        </sdx-select-option>
        <sdx-select-option value="x% erneuerbar" selected={formData.strommix === "x% erneuerbar"}>
          x% erneuerbar
        </sdx-select-option>
      </sdx-select>

      {formData.strommix === 'x% erneuerbar' && (
        <div style={{ marginTop: '0.75rem' }}>
          <label className="label" style={{ display: 'block', marginBottom: '0.5rem' }}>
            Anteil erneuerbar (%)
          </label>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <input
              id="renewRange"
              type="range"
              min="0"
              max="100"
              step="1"
              value={formData.renewableShare ?? ''}
              onInput={(e) => {
                handleChange('renewableShare', Number(e.target.value));
                setTouched(prev => ({ ...prev, renewableShare: true }))
                console.log(touched)
              }}
              aria-label="Anteil erneuerbar in Prozent"
              style={{
                flex: 1,
                accentColor: INTERACTION
              }}
            />
            <label htmlFor="renewRange" style={{ width: '3.5ch', textAlign: 'right' }}>
              {(formData.renewableShare ?? 0)}%
            </label>
          </div>
          {showError && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "4px",
                color: "#d12",
                fontSize: "16px",
                marginTop: "6px",
                marginLeft: "-8px"
              }}
            >
              <span
                className="icon icon-exclamation-mark icon--s2"
                aria-hidden="true"
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: "16px",     // 👈 fixiert Breite
                  minWidth: "16px"   // 👈 verhindert Verschieben
                }}
              ></span>

              <span style={{ margin: 0 }}>Pflichtfeld</span>
            </div>
          )}
        </div>
      )}

      <sdx-input
        label="Menge in kWh"
        type="text"
        inputmode="decimal"
        value={formatNumericInput(formData.mengeStrom ?? "")}
        onInput={(e) => handleChange('mengeStrom', cleanNumericInput(e.target.value))}
        onBlur={() => setTouched(prev => ({ ...prev, mengeStrom: true }))}
        valid={touched.mengeStrom && !formData.mengeStrom ? "false" : undefined}
        validation-message={touched.mengeStrom && !formData.mengeStrom ? "Pflichtfeld" : undefined}

      ></sdx-input>

      <sdx-select
        label="Besitzen Sie eine eigene PV Anlage?"
        placeholder="Auswählen…"
        onInput={(e) => {
          handleChange('pvOwn', readSdxValue(e))
          setTouched(prev => ({ ...prev, pvOwn: true }))
        }}
        valid={touched.pvOwn && !formData.pvOwn ? "false" : undefined}
        validation-message={touched.pvOwn && !formData.pvOwn ? "Pflichtfeld" : undefined}

        style={{ marginTop: '1rem' }}
      >
        <sdx-select-option value="Ja" selected={formData.pvOwn === "Ja"}>Ja</sdx-select-option>
        <sdx-select-option value="Nein" selected={formData.pvOwn === "Nein"}>Nein</sdx-select-option>
      </sdx-select>

      {formData.pvOwn === 'Ja' && (
        <sdx-input
          label="PV-Menge (kWh)"
          type="text"
          inputmode="decimal"
          value={formatNumericInput(formData.pvMengeKwh ?? "")}
          onInput={(e) => handleChange('pvMengeKwh', cleanNumericInput(e.target.value))}
          onBlur={() => setTouched(prev => ({ ...prev, pvMengeKwh: true }))}
          valid={touched.pvMengeKwh && !formData.pvMengeKwh ? "false" : undefined}
          validation-message={touched.pvMengeKwh && !formData.pvMengeKwh ? "Pflichtfeld" : undefined}

        ></sdx-input>
      )}

      <h3
        className="h3"
        style={{
          marginTop: "2rem",
          color: BRAND
        }}
      >
        Einkauf
      </h3>
      <sdx-input
        label="Lack in Liter"
        type="text"
        inputmode="decimal"
        value={formatNumericInput(formData.verbrauchsmaterialLack ?? "")}
        onInput={(e) => handleChange('verbrauchsmaterialLack', cleanNumericInput(e.target.value))}
      ></sdx-input>
      <sdx-input
        label="Verbrauchsmaterialien in CHF"
        type="text"
        inputmode="decimal"
        value={formatNumericInput(formData.verbrauchsmaterialAllg ?? "")}
        onInput={(e) => handleChange('verbrauchsmaterialAllg', cleanNumericInput(e.target.value))}
      ></sdx-input>
      <sdx-input
        label="Ersatzteile in CHF"
        type="text"
        inputmode="decimal"
        value={formatNumericInput(formData.verbrauchsmaterialErsatzteile ?? "")}
        onInput={(e) => handleChange('verbrauchsmaterialErsatzteile', cleanNumericInput(e.target.value))}
      ></sdx-input>

      <h3
        className="h3"
        style={{
          marginTop: "1.5rem",
          color: BRAND
        }}
      >
        Lack
      </h3>

      <sdx-select
        label="Lackmix"
        placeholder="Auswählen…"
        onInput={(e) => setDetailsData(prev => ({ ...prev, lackmix: readSdxValue(e) }))}
      >
        <sdx-select-option value="konventioneller Lack" selected={detailsData.lackmix === "konventioneller Lack"}>
          konventioneller Lack
        </sdx-select-option>
        <sdx-select-option value="x% lufttrocknender Lack" selected={detailsData.lackmix === "x% lufttrocknender Lack"}>
          x% lufttrocknender Lack
        </sdx-select-option>
      </sdx-select>

      {detailsData.lackmix === 'x% lufttrocknender Lack' && (
        <div style={{ marginTop: '0.75rem' }}>
          <label className="label" style={{ display: 'block', marginBottom: '0.5rem' }}>
            Anteil lufttrocknender Lack (%)
          </label>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <input
              id="renewRange"
              type="range"
              min="0"
              max="100"
              step="1"
              value={detailsData.lufttrocknendShare == null || detailsData.lufttrocknendShare === '' ? 0 : detailsData.lufttrocknendShare}
              onInput={(e) => setDetailsData(prev => ({ ...prev, lufttrocknendShare: Number(e.target.value) }))}
              aria-label="Anteil lufttrocknender Lack in Prozent"
              style={{
                flex: 1,
                accentColor: INTERACTION
              }}
            />
            <label htmlFor="renewRange" style={{ width: '3.5ch', textAlign: 'right' }}>
              {(detailsData.lufttrocknendShare ?? 0)}%
            </label>
          </div>
        </div>
      )}

      <h3
        className="h3"
        style={{
          marginTop: "2rem",
          color: BRAND
        }}
      >
        Abfall
      </h3>
      <sdx-input
        label="Menge in Tonnen"
        type="text"
        inputmode="decimal"
        value={formatNumericInput(detailsData.abfallTonnen ?? "")}
        onInput={(e) => setDetailsData(prev => ({ ...prev, abfallTonnen: cleanNumericInput(e.target.value) }))}
      ></sdx-input>

      {/* Zwei Wege anbieten */}
      <div style={{ display: "flex", gap: "0.75rem", marginTop: "2rem" }}>
        <sdx-button
          variant="primary"
          label="Weiter zu Übersicht"
          onClick={() => {
            const heatingValid = areRowsValid(heatingRows, ['area', 'carrier', 'unit', 'amount']);
            const fuelValid = areRowsValid(fuelRows, ['type', 'unit', 'amount']);

            // Pflichtfelder als touched markieren
            setTouched(prev => ({
              ...prev,
              bereiche: true,
              jahr: true,
              mitarbeitende: true,
              produktiveMitarbeitende: true,
              strommix: true,
              mengeStrom: true,
              pvOwn: true,
              pvMengeKwh: formData.pvOwn === 'Ja' ? true : prev.pvMengeKwh,
              renewableShare: true,
            }));

            // Zeilen als touched markieren
            setTouchedRows(prev => ({
              ...prev,
              heating: Object.fromEntries(heatingRows.map(r => [r.id, true])),
              fuel: Object.fromEntries(fuelRows.map(r => [r.id, true])),
            }));

            // Nur weiter wenn alles gültig
            if (!heatingValid || !fuelValid || isBereicheInvalid() || isJahrInvalid()
              || isMitarbeitendeInvalid() || isProduktiveMitarbeitendeInvalid() || !isStromValid() || isRenewableShareInvalid()) {
              showAppNotification({ message: "Bitte alle Pflichtfelder ausfüllen.", type: "warning" });
              return;
            }

            setSnapshot({ formData, heatingRows, fuelRows, detailsData, lackierkabinenRows, fossilRows, lightRows });
            setMode('review');
          }}
        ></sdx-button>
        <sdx-button
          theme="secondary"
          label="Weiter zu Details"
          onClick={() => {
            const heatingValid = areRowsValid(heatingRows, ['area', 'carrier', 'unit', 'amount']);
            const fuelValid = areRowsValid(fuelRows, ['type', 'unit', 'amount']);

            // Alle Pflichtfelder als touched markieren
            setTouched(prev => ({
              ...prev,
              bereiche: true,
              jahr: true,
              mitarbeitende: true,
              produktiveMitarbeitende: true,
              strommix: true,
              mengeStrom: true,
              pvOwn: true,
              pvMengeKwh: formData.pvOwn === 'Ja' ? true : prev.pvMengeKwh,
              renewableShare: true
            }));

            setTouchedRows(prev => ({
              ...prev,
              heating: Object.fromEntries(heatingRows.map(r => [r.id, true])),
              fuel: Object.fromEntries(fuelRows.map(r => [r.id, true])),
            }));

            // Blockieren wenn ungültig
            if (!heatingValid || !fuelValid || isBereicheInvalid() || isJahrInvalid()
              || isMitarbeitendeInvalid() || isProduktiveMitarbeitendeInvalid() || !isStromValid() || isRenewableShareInvalid()) {
              showAppNotification({ message: "Bitte alle Pflichtfelder ausfüllen.", type: "warning" });
              return;
            }

            setSnapshot({ formData, heatingRows, fuelRows, detailsData, lackierkabinenRows, fossilRows, lightRows });
            setMode('details');
          }}
        ></sdx-button>
      </div>
    </div>
  );
}
